import { useMemo } from "react";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";

import { useDateRangeStore } from "./date-range-picker/use-date-range-store";
import {
  getAllClosedLeadLogsByDateQueryOptions,
  getAllEmployeesForSAQueryOptions,
} from "@/config/queries/superadmin-query-options";
import { convertUTCtoIST } from "@/utils";
import { DateRangePicker } from "./date-range-picker/date-range-picker2";
import { Bar } from "react-chartjs-2";

// Register the zoom plugin
ChartJS.register(zoomPlugin);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const LeadsClosedChart = () => {
  const user = Cookies.get("key") as string;

  const { data, isPending, isFetching } = useQuery(
    getAllEmployeesForSAQueryOptions(user)
  );

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        id: emp?._id,
        name: emp?.name,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  const { dateRange } = useDateRangeStore();

  const startdate = dateRange?.from as Date;
  const enddate = dateRange?.to as Date;

  const {
    data: allLeadLogsResponse,
    isPending: isLeadLogsPending,
    isFetching: isLeadLogsFetching,
  } = useQuery(
    getAllClosedLeadLogsByDateQueryOptions(
      user,
      convertUTCtoIST(new Date(startdate.setHours(0))),
      convertUTCtoIST(new Date(enddate.setHours(23)))
    )
  );

  console.log("logs", allLeadLogsResponse);

  const completeChartData = useMemo(() => {
    const labels = employeesData?.map((emp) => emp?.id);
    // let datasets = employeesData?.map((emp) => {
    //   return {
    //     id: emp?.id,
    //     label: emp?.name,
    //     data: Array(labels.length).fill(0),
    //   };
    // });

    const datasets: {
      label: string;
      data: number[];
      backgroundColor: string;
      borderWidth: number;
      barThickness: number;
    }[] = [
      {
        label: "Lead Closed",
        data: Array(labels.length).fill(0),
        backgroundColor: "rgba(255, 99, 132, 0.5)",
        borderWidth: 1,
        barThickness: 40,
      },
    ];

    if (isLeadLogsPending) {
      return {
        labels: employeesData?.map((emp) => emp?.name),
        datasets,
      };
    }

    if (
      allLeadLogsResponse === undefined ||
      allLeadLogsResponse.logs === undefined
    ) {
      return {
        labels: employeesData?.map((emp) => emp?.name),
        datasets,
      };
    }

    const leadLogs = allLeadLogsResponse?.logs;

    for (const leadLog of leadLogs) {
      const findEmp = labels.findIndex((it) => it === leadLog?.createdBy?._id);

      const prevData = datasets?.[0]?.data;

      const newData = prevData?.map((d, index) => {
        if (index === findEmp) {
          return d + 1;
        }

        return d;
      });

      datasets[0].data = newData;
    }

    return {
      labels: employeesData?.map((emp) => emp?.name),
      datasets,
    };
  }, [
    employeesData,
    allLeadLogsResponse,
    isLeadLogsFetching,
    isLeadLogsPending,
  ]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-medium">
          Report 2: Number of Leads closed by employees
        </h1>

        <DateRangePicker />
      </div>

      <div>
        <h3>Total closed leads: {allLeadLogsResponse?.logs?.length}</h3>
      </div>

      <div className="h-[70vh]">
        <Bar
          data={completeChartData}
          options={{
            scales: {
              y: {
                min: 0,
              },
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              zoom: {
                pan: {
                  enabled: true,
                  mode: "x",
                },
                zoom: {
                  wheel: {
                    enabled: true,
                  },
                  pinch: {
                    enabled: true,
                  },
                  mode: "x",
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};
